import * as React from 'react'
import { Button, Collapse, Intent, Position } from '@blueprintjs/core'
import { useEffect } from 'react'
import { UprightStyle } from '../../../../../types'
import { Specific, SpecificsProps } from '../types'
import { GutterTooltipError } from '../GutterTooltipError'
import { renderError } from '../Helpers'
import SpecificRenderer from './SpecificRenderer'
import * as EbayInputs from './Inputs'
import { pluralizeFromNumber } from '../../../../../utils/string'
import Spinner from '../../../../../components/Spinner'

const specificsStyles: UprightStyle = {
  loadingSpinner: { textAlign: 'left' },
  topPadding: { paddingTop: 10 }
}

const checkNonRequiredSpecificsAlreadyFilled = (requirements, form) => {
  if (requirements.specifics.length === 0 && Object.keys(form.specifics).length === 0) return false

  let foundNonRequiredSpecifics = false
  const specificValueMap = form.specifics

  const specifics = (requirements.specifics && requirements.specifics.map((s, i) => ({ ...s, originalIndex: i }))) || []
  const nonRequiredSpecifics = specifics.filter(o => !o.required)

  Object.keys(specificValueMap).forEach(key => {
    if (nonRequiredSpecifics.find(s => s.name === key)) {
      if (specificValueMap[key] !== null && specificValueMap[key] !== undefined && specificValueMap[key] !== '')
        foundNonRequiredSpecifics = true
    }
  })

  return foundNonRequiredSpecifics
}

export const EbaySpecifics: React.FC<SpecificsProps & { hideOptionalSpecifics?: boolean }> = ({
  form,
  requirements,
  errors,
  onFormChange,
  onSpecificChange,
  onProductDetailChange,
  onRefreshSpecifics,
  loadingRefreshSpecifics,
  sectionOpen,
  nonRequiredSpecificsOpenAtFirst = false,
  hideOptionalSpecifics = false
}) => {
  const newShouldShowNonRequiredSpecifics = React.useMemo(
    () => checkNonRequiredSpecificsAlreadyFilled(requirements, form),
    [requirements, form]
  )
  const [nonRequiredSpecificsOpen, setNonRequiredSpecificsOpen] = React.useState(nonRequiredSpecificsOpenAtFirst)

  useEffect(() => {
    if (newShouldShowNonRequiredSpecifics) {
      // If the template has already pre-filled some optional specific, show the optional specifics to the user
      setNonRequiredSpecificsOpen(true)
    }
  }, [newShouldShowNonRequiredSpecifics])

  const specifics: Specific[] = React.useMemo(
    () => (requirements.specifics && requirements.specifics.map((s, i) => ({ ...s, originalIndex: i }))) || [],
    [requirements]
  )
  const nonRequiredSpecifics: Specific[] = React.useMemo(() => specifics.filter(o => !o.required), [specifics])
  const requiredSpecifics: Specific[] = React.useMemo(() => specifics.filter(o => o.required), [specifics])

  const specificsCount = React.useMemo(() => {
    return (
      nonRequiredSpecifics.length +
      [requirements.upc, requirements.ean, requirements.isbn]
        .map(detail => (!detail ? 0 : 1))
        .reduce((acc: number, detailCount: number) => {
          return acc + detailCount
        }, 0)
    )
  }, [nonRequiredSpecifics, requirements])

  const specificValueMap = form.specifics
  if (requirements.loading) {
    return (
      <>
        <div className="stretch-full specifics-heading">
          <h4 className="span-4">Required Specifics</h4>
        </div>
        <Spinner style={specificsStyles.loadingSpinner} />
      </>
    )
  }

  return (
    <>
      <div className="stretch-full specifics-heading">
        <h4 className="span-2">Required Specifics</h4>
        {form.categoryMarketCategory && form.categoryMarketCategory.id && !hideOptionalSpecifics && (
          <Button
            intent={Intent.PRIMARY}
            minimal
            onClick={() => {
              analytics.track('Channel Form Section Toggle', {
                section: 'ebay specifics section',
                setOpen: !nonRequiredSpecificsOpen
              })
              setNonRequiredSpecificsOpen(!nonRequiredSpecificsOpen)
            }}
          >
            {nonRequiredSpecificsOpen ? 'Hide' : 'Show'} {specificsCount} Optional Specific
            {pluralizeFromNumber(specificsCount)}
          </Button>
        )}
        <Button
          text="Refresh"
          onClick={onRefreshSpecifics}
          loading={loadingRefreshSpecifics}
          minimal
          intent={Intent.PRIMARY}
        />
      </div>
      {(errors?.ebayListingSpecifics || errors.localErrors?.specifics) && (
        <p className="stretch-full">
          <GutterTooltipError isOpen offsetPx={sectionOpen && 84} position={Position.LEFT}>
            {renderError(errors.ebayListingSpecifics ? errors.ebayListingSpecifics : errors.localErrors.specifics)}
          </GutterTooltipError>
        </p>
      )}
      {requiredSpecifics.map(ss => {
        return (
          <div className="span-4 react-select-same-height-as-blueprint" key={ss.name}>
            <SpecificRenderer
              createable={ss.validationRules.selectionMode === 'FreeText'}
              categoryMarketCategoryId={form.categoryMarketCategory.id}
              required={ss.required}
              allowDangerIntent={errors.ebayListingSpecifics}
              value={specificValueMap[ss.name]}
              specificName={ss.name}
              onChange={(v: string) => onSpecificChange(v, ss.originalIndex)}
              relevanceSearchCount={ss.relevanceSearchCount}
              constrainedBy={ss.constrainedBy}
              siblingValues={specificValueMap}
            />
          </div>
        )
      })}
      {requirements.conditions && requirements.conditions.length > 0 && (
        <div className="span-4 react-select-same-height-as-blueprint">
          <EbayInputs.ConditionPicker
            value={form.condition}
            options={requirements.conditions}
            error={errors.ebayListingCondition}
            onChange={onFormChange}
            errorTooltipOffset={sectionOpen && 45 + (requiredSpecifics.length % 3) * (419 - 45)}
          />
        </div>
      )}

      <Collapse
        isOpen={!hideOptionalSpecifics && nonRequiredSpecificsOpen}
        keepChildrenMounted
        className={nonRequiredSpecificsOpen ? 'stretch-full' : ''}
      >
        <div className="specifics-form-grid" style={specificsStyles.topPadding}>
          {!hideOptionalSpecifics &&
            nonRequiredSpecifics.map(ss => (
              <div className="span-4 react-select-same-height-as-blueprint" key={ss.name}>
                <SpecificRenderer
                  createable={ss.validationRules.selectionMode === 'FreeText'}
                  categoryMarketCategoryId={form.categoryMarketCategory.id}
                  required={false}
                  value={specificValueMap[ss.name]}
                  specificName={ss.name}
                  onChange={(v: string) => onSpecificChange(v, ss.originalIndex)}
                  relevanceSearchCount={ss.relevanceSearchCount}
                  constrainedBy={ss.constrainedBy}
                  siblingValues={specificValueMap}
                />
              </div>
            ))}
        </div>
      </Collapse>
      {!hideOptionalSpecifics && (
        <>
          <EbayInputs.ProductListingDetail
            label="UPC"
            detail={requirements.upc}
            value={form.productListingDetails.upc}
            name="upc"
            onChange={onProductDetailChange}
            className="span-4"
          />
          <EbayInputs.ProductListingDetail
            label="EAN"
            detail={requirements.ean}
            value={form.productListingDetails.ean}
            name="ean"
            onChange={onProductDetailChange}
            className="span-4"
          />
          <EbayInputs.ProductListingDetail
            label="ISBN"
            detail={requirements.isbn}
            value={form.productListingDetails.isbn}
            name="isbn"
            onChange={onProductDetailChange}
            className="span-4"
          />
        </>
      )}
    </>
  )
}
